import { useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import eq from "lodash-es/eq"
import { DateTime } from "luxon"
import { useClickAway, useNumber, useUpdateEffect } from "react-use"
import type { CounterActions } from "react-use/lib/useCounter"

import {
    CopyButton,
    ECopyButtonVariant,
    ETypographyColor,
    ETypographySize,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import {
    type TScreenBreakpoints,
    type TUseInteractionState,
    useInteractionState,
    useScreenBreakpoints
} from "@/3514/hooks"
import { getCssIdentifierFromFileName, renderRichContent } from "@/3514/utils"

import { coachingChatStyles as chatStyles } from "../styles"
import { ECoachingChatSummaryCardMode as EMode, type TCoachingChatSummaryCard as TModel } from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

type TProps = TModel & { cardsAmount: number; mode: EMode }

const displayName: string = "CoachingChatSummaryCard"

function getEditorClassNameIdentifier(prefix: string): string {
    return getCssIdentifierFromFileName(displayName, `ed-${prefix}`)
}

const {
    insertionClassesUtils: { getChatSummaryCardContentCssString: getCardCssString }
} = uiUtils

const {
    conversation: { summaryCard: classes }
} = chatStyles

const Card: FC<TProps> = ({ title, content, completedAt = undefined, cardsAmount, mode }: TProps): ReactElement => {
    const isChatMode: boolean = eq(mode, EMode.BuildInChat)

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const {
        isActionAffected,
        actions: { onMouseEnter, onMouseLeave, onMouseMove, __setter: setIsActionAffected }
    }: TUseInteractionState = useInteractionState()

    const cardRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const contentHolderRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const headingRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const [cardBodyHeight, { set: setCardBodyHeight }]: [number, CounterActions] = useNumber(45)

    useUpdateEffect(
        (): void => (
            isActionAffected
                ? requestAnimationFrame((): void =>
                      setCardBodyHeight(
                          Number(contentHolderRef.current?.scrollHeight) +
                              (Number(headingRef?.current?.children?.[0]?.scrollHeight) < 30
                                  ? 30
                                  : Number(headingRef?.current?.children?.[0]?.scrollHeight)) +
                              30
                      )
                  )
                : setCardBodyHeight(45),
            void 0
        ),
        [isActionAffected]
    )

    useClickAway(cardRef, (): void =>
        !breakpoints.lg && !breakpoints.xl && !breakpoints.xxl
            ? isActionAffected
                ? setIsActionAffected(false)
                : void 0
            : void 0
    )

    return (
        <div
            ref={cardRef}
            className={classes.card(mode, cardsAmount)}
            {...(!isChatMode && {
                ...((breakpoints.lg || breakpoints.xl || breakpoints.xxl) && {
                    onMouseEnter,
                    onMouseLeave,
                    onMouseMove
                }),
                ...(!breakpoints.xl &&
                    !breakpoints.xxl &&
                    !breakpoints.lg && {
                        onClick: (): void => setIsActionAffected(true)
                    })
            })}
        >
            <div
                className={classes.cardBody(mode, isChatMode ? false : isActionAffected)}
                {...(!isChatMode && {
                    style: {
                        height: `${cardBodyHeight}px`
                    }
                })}
            >
                <div className={classes.cardHeading(mode)} ref={headingRef}>
                    <Typography
                        color={isChatMode ? ETypographyColor.White : ETypographyColor.Black}
                        weight={ETypographyWeight.Bold}
                        size={ETypographySize.Small}
                        className={classes.cardTitleLabel(mode, isChatMode ? true : isActionAffected)}
                        text={title}
                    />
                    <div className={classes.cardCopyContainer()}>
                        {(isChatMode || isActionAffected) && (
                            <CopyButton
                                valueToCopy={content}
                                className={classes.cardIcon(mode, isChatMode ? false : isActionAffected)}
                                variant={isChatMode ? ECopyButtonVariant.Gray : ECopyButtonVariant.Blue}
                            />
                        )}
                        {!isChatMode && isActionAffected && DateTime.isDateTime(DateTime.fromISO(completedAt)) && (
                            <Typography
                                isItalic
                                text={uiUtils.getFormattedSummaryCardDate(completedAt)}
                                className={classes.cardDate(mode)}
                                size={ETypographySize.Tiny}
                                color={ETypographyColor.Gray23}
                            />
                        )}
                    </div>
                </div>
                <div
                    ref={contentHolderRef}
                    className={classes.cardContentWrapper(mode, isChatMode ? false : isActionAffected)}
                >
                    {renderRichContent({
                        identifier: getEditorClassNameIdentifier(isChatMode ? "ch" : "ins"),
                        classes: getCardCssString(getEditorClassNameIdentifier(isChatMode ? "ch" : "ins"), mode),
                        editorClasses: classes.cardContentEditor()
                    })(content)}
                </div>
            </div>
        </div>
    )
}

Card.displayName = displayName

export { Card as CoachingChatSummaryCard }
